// The toolbar: search, the filter triggers, then the top pager and View options
// at its end. It measures the pixels the filter group may take (More filters
// included, search at its minimum width) and reports them to the shell, which
// publishes them as geometry.toolbarFreeWidth.

import React, { useEffect, useLayoutEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import { useTable } from '../context'
import { Search } from './Search'

export const TOOLBAR_GAP = 8
const FILTER_GROUP = 'me-table-filters'
const SEARCH = 'me-search-field'

export const widthOf = element =>
  element ? element.getBoundingClientRect().width || 0 : 0

export const pixels = (value, fallback) => {
  const number = parseFloat(value)
  return Number.isFinite(number) ? number : fallback
}

/**
 * The width the filter group may take: the toolbar's content width less every
 * other toolbar control and the gaps beside them, search at its minimum width.
 * The shell publishes 0 before layout; the filter group's own fallback passes
 * Infinity, so an unmeasured toolbar folds nothing.
 * @param {?HTMLElement} toolbar
 * @param {number} [unmeasured] the width while the toolbar has not been laid out
 * @returns {number}
 */
export const toolbarFreeWidth = (toolbar, unmeasured = 0) => {
  if (!toolbar) return unmeasured
  const style = window.getComputedStyle(toolbar)
  const inner =
    widthOf(toolbar) -
    pixels(style.paddingLeft, 0) -
    pixels(style.paddingRight, 0)
  if (!(inner > 0)) return unmeasured
  const gap = pixels(style.columnGap, TOOLBAR_GAP)
  let used = 0
  let others = 0
  Array.from(toolbar.children).forEach(child => {
    if (child.classList.contains(FILTER_GROUP)) return
    others += 1
    if (child.classList.contains(SEARCH)) {
      const min = pixels(window.getComputedStyle(child).minWidth, 0)
      used += min > 0 ? min : widthOf(child)
    } else used += widthOf(child)
  })
  return Math.max(0, Math.floor(inner - used - gap * others))
}

export const Toolbar = ({ onFreeWidth = null }) => {
  const { layout, slots } = useTable()
  const { FilterBar, ViewOptions, TopPager } = slots
  const ref = useRef(null)
  const report = useRef(onFreeWidth)
  report.current = onFreeWidth

  const measure = () => {
    if (typeof report.current === 'function')
      report.current(toolbarFreeWidth(ref.current))
  }

  // every render can change what the other controls take
  useLayoutEffect(measure)

  useEffect(() => {
    const toolbar = ref.current
    if (!toolbar) return undefined
    let observer = null
    if (typeof window.ResizeObserver === 'function') {
      observer = new window.ResizeObserver(() => measure())
      observer.observe(toolbar)
      Array.from(toolbar.children).forEach(child => {
        if (!child.classList.contains(FILTER_GROUP)) observer.observe(child)
      })
    }
    window.addEventListener('resize', measure)
    return () => {
      if (observer) observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [layout.searchable])

  return (
    <div ref={ref} className="me-table-toolbar">
      {layout.searchable && <Search />}
      <FilterBar />
      <div className="me-table-toolbar-end">
        <TopPager />
        <ViewOptions />
      </div>
    </div>
  )
}

Toolbar.propTypes = {
  // receives the pixels the filter group may take after each layout
  onFreeWidth: PropTypes.func,
}
